import { useRef, useState, useCallback, useEffect } from 'react'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import {
  selectNavSite,
  toggleNavSite,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import NavSiteOverlay from './NavSiteOverlay'
import NavSiteContent from './NavSiteContent'

const NavSite = () => {
  const dispatch = useAppDispatch()
  const navRef = useRef<HTMLElement | null>(null)
  const [active, setActive] = useState<Element | null>(null)
  const [elements, setElements] = useState<Element[]>([])
  const { isOpen } = useAppSelector(selectNavSite)

  const handleExit = () => {
    if (active) (active as any).focus()
  }

  const handleFocus = () => {
    setActive(document.activeElement)
    const allElements = navRef.current?.querySelectorAll(
      'a[href], button, input, select, textarea'
    )
    const elements = Array.from(allElements ?? [])
    setElements(elements)
    const firstElement = allElements ? allElements[0] : null
    if (firstElement) (firstElement as any).focus()
  }

  const handleKeys = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.keyCode === 9 && navRef.current && elements.length) {
        const activeElements = Array.from(elements).filter(
          (i: any) => !i.disabled
        )
        const firstElement = activeElements[0]
        const lastElement = activeElements[activeElements.length - 1]

        if (!evt.shiftKey && document.activeElement === lastElement) {
          ;(firstElement as any).focus()
          evt.preventDefault()
        }

        if (evt.shiftKey && document.activeElement === firstElement) {
          ;(lastElement as any).focus()
          evt.preventDefault()
        }
      } else if (evt.keyCode === 27 && isOpen) {
        dispatch(toggleNavSite())
      }
    },
    [elements, isOpen, dispatch]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeys, false)
    return () => document.removeEventListener('keydown', handleKeys, false)
  }, [handleKeys])

  return (
    <>
      <NavSiteOverlay />
      <TransitionGroup component={null}>
        {isOpen ? (
          <CSSTransition
            key="sidebar"
            classNames="sidebar"
            timeout={{ enter: 250, exit: 250 }}
            onEntered={handleFocus}
            onExited={handleExit}
          >
            <NavSiteContent ref={navRef} />
          </CSSTransition>
        ) : null}
      </TransitionGroup>
    </>
  )
}

export default NavSite
